import React, { useEffect, useState } from "react";
import { getAllBets, deleteBet, deleteAllBets } from "../../../../Services/Events";
import { toast } from "react-toastify";

const UserBets = ({ eventId }) => {
  const [bets, setBets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  useEffect(() => {
    fetchBets();
  }, [eventId]);

  const fetchBets = async () => {
    if (!eventId) {
      setError("No event ID provided");
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const response = await getAllBets(eventId);
      
      if (response.data.status === 1) {
        setBets(response.data.data.responseData);
      } else {
        const errorMessage = response.data.message || "Failed to fetch bets";
        setError(errorMessage);
        toast.error(errorMessage);
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || "Error fetching bets";
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteBet = async (betId) => {
    if (!betId) {
      toast.error("Invalid bet ID");
      return;
    }

    if (window.confirm("Are you sure you want to delete this bet?")) {
      try {
        await deleteBet(betId);
        toast.success("Bet deleted successfully");
        fetchBets(); // Refresh the bets list
      } catch (error) {
        toast.error(error.response?.data?.message || "Error deleting bet");
      }
    }
  };

  const handleDeleteAllBets = async () => {
    if (!eventId) {
      toast.error("Invalid event ID");
      return;
    }

    if (window.confirm("Are you sure you want to delete all bets for this event? This action cannot be undone.")) {
      try {
        await deleteAllBets(eventId);
        toast.success("All bets deleted successfully");
        fetchBets(); // Refresh the bets list
      } catch (error) {
        toast.error(error.response?.data?.message || "Error deleting all bets");
      }
    }
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortedBets = () => {
    if (!Array.isArray(bets)) return [];

    let sortedBets = [...bets];
    if (sortConfig.key) {
      sortedBets.sort((a, b) => {
        const aValue = a[sortConfig.key] || '';
        const bValue = b[sortConfig.key] || '';
        
        if (aValue < bValue) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortedBets;
  };

  if (loading) {
    return <div className="text-center p-4">Loading bets...</div>;
  }

  if (error) {
    return (
      <div className="alert alert-danger" role="alert">
        {error}
      </div>
    );
  }

  const sortedBets = getSortedBets();

  return (
    <>
      <div className="page_header d-flex justify-content-between align-items-center">
        <h2>User Bets</h2>
        <button
          type="button"
          onClick={handleDeleteAllBets}
          className="btn btn-danger"
          disabled={!eventId || bets.length === 0}
        >
          Delete All Bets
        </button>
      </div>

      <div className="table-responsive">
        <table className="table table-bordered">
          <thead className="table-light">
            <tr>
              <th onClick={() => handleSort('user_name')} style={{ cursor: 'pointer' }}>
                User {sortConfig.key === 'user_name' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleSort('user_email')} style={{ cursor: 'pointer' }}>
                Email {sortConfig.key === 'user_email' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleSort('question')} style={{ cursor: 'pointer' }}>
                Question {sortConfig.key === 'question' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleSort('selected_choice')} style={{ cursor: 'pointer' }}>
                Selected Choice {sortConfig.key === 'selected_choice' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleSort('bet_price')} style={{ cursor: 'pointer' }}>
                Bet Price {sortConfig.key === 'bet_price' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleSort('currency_type')} style={{ cursor: 'pointer' }}>
                Currency {sortConfig.key === 'currency_type' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleSort('question_status')} style={{ cursor: 'pointer' }}>
                Status {sortConfig.key === 'question_status' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleSort('created_at')} style={{ cursor: 'pointer' }}>
                Created At {sortConfig.key === 'created_at' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
              </th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {sortedBets.map((bet) => (
              <tr key={bet.bet_id}>
                <td>{bet.user_name || "Unknown User"}</td>
                <td>{bet.user_email}</td>
                <td>{bet.question || "Unknown Question"}</td>
                <td>{bet.selected_choice || "No choice selected"}</td>
                <td>{bet.bet_price || "N/A"}</td>
                <td>{bet.currency_type || "N/A"}</td>
                <td>
                  <span className={`badge ${bet.question_status === 'Open' ? 'bg-success' : 'bg-danger'}`}>
                    {bet.question_status || "Unknown"}
                  </span>
                </td>
                <td>{new Date(bet.created_at).toLocaleString()}</td>
                <td>
                  <button
                    className="btn btn-sm btn-danger"
                    onClick={() => handleDeleteBet(bet.bet_id)}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
            {bets.length === 0 && (
              <tr>
                <td colSpan="9" className="text-center">
                  No bets found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </>
  );
};

export default UserBets; 